/** Client-side mirror of backend/trg_digital/services/totals.py, for instant feedback while typing. NOT authoritative: persisted figures come from the server.
 *  The editor shows THIS figure for a draft, so it must agree with the server to the cent; the shared fixture `pages/sales/salesTotalsParity.json` pins it case by case. Change one side, change the other and regenerate the fixture.
 *  `computeTotals` runs on exact BigInt decimals, never floats. */

export type SalesType = 'Sales Invoice' | 'Quotation' | 'Inventory expenditure order'
export type DiscountType = 'None' | 'Amount' | 'Percentage'
export type VatType = '0%' | '10%'

export interface LineInput {
  qty: number
  unitPrice: number
}

export interface Totals {
  subtotal: number
  discountValue: number
  net: number
  vat: number
  total: number
}

/** Round(x, 2) half-up, for display helpers only (`roundMoney`). Float-based, so a half-cent tie can land a cent low; `computeTotals` never uses it. */
function round2(value: number): number {
  return Math.round(value * 100) / 100
}

/* Exact decimal arithmetic for `computeTotals`, mirroring Python's `Decimal`: the float version disagreed with `services/totals.py` on half-cent ties.
 * A value is `n / 10^s` with `n` a BigInt. A JS number enters through `String(n)`, the same shortest round-trip digits Pydantic reads, so client and server start from one value. */
interface Dec {
  n: bigint
  s: number
}

export function dec(value: number): Dec {
  if (!Number.isFinite(value)) return { n: 0n, s: 0 }
  const [mantissa, expPart] = String(value).toLowerCase().split('e')
  const exp = expPart ? Number(expPart) : 0
  const negative = mantissa.startsWith('-')
  const [whole, frac = ''] = mantissa.replace('-', '').split('.')
  let n = BigInt(whole + frac)
  let s = frac.length - exp
  if (s < 0) {
    n *= 10n ** BigInt(-s)
    s = 0
  }
  return { n: negative ? -n : n, s }
}

function align(a: Dec, b: Dec): [bigint, bigint, number] {
  const s = Math.max(a.s, b.s)
  return [a.n * 10n ** BigInt(s - a.s), b.n * 10n ** BigInt(s - b.s), s]
}

const add = (a: Dec, b: Dec): Dec => {
  const [x, y, s] = align(a, b)
  return { n: x + y, s }
}
const sub = (a: Dec, b: Dec): Dec => add(a, { n: -b.n, s: b.s })
const mul = (a: Dec, b: Dec): Dec => ({ n: a.n * b.n, s: a.s + b.s })
/** Divide by 10^k exactly (VAT is x 0.1, a percentage is / 100). */
const shift = (a: Dec, k: number): Dec => ({ n: a.n, s: a.s + k })
const cmp = (a: Dec, b: Dec): number => {
  const [x, y] = align(a, b)
  return x < y ? -1 : x > y ? 1 : 0
}
const min = (a: Dec, b: Dec): Dec => (cmp(a, b) <= 0 ? a : b)
const max = (a: Dec, b: Dec): Dec => (cmp(a, b) >= 0 ? a : b)

/** The decimal places a document may round its amounts to (`sales_document.rounding_places`). */
export const ROUNDING_PLACES = [2, 1, 0] as const
export type RoundingPlaces = (typeof ROUNDING_PLACES)[number]

/** `round_amount` in totals.py: half-up (ties away from zero) to `places`, held at the 2dp scale the columns store. */
function qn(a: Dec, places: RoundingPlaces = 2): Dec {
  let r: Dec
  if (a.s <= places) {
    r = a
  } else {
    const div = 10n ** BigInt(a.s - places)
    const negative = a.n < 0n
    const abs = negative ? -a.n : a.n
    let n = abs / div
    if ((abs % div) * 2n >= div) n += 1n
    r = { n: negative ? -n : n, s: places }
  }
  return { n: r.n * 10n ** BigInt(2 - r.s), s: 2 }
}

const q2 = (a: Dec): Dec => qn(a, 2)

/** A 2dp Dec as a number: exact, since it is at most a 2dp decimal string. */
function num(a: Dec): number {
  return Number(q2(a).n) / 100
}

const ZERO: Dec = { n: 0n, s: 0 }

/** Half-up 2dp rounding for editors showing a per-row amount or running total. Display only; the server is authoritative. */
export function roundMoney(value: number): number {
  return round2(value)
}

/** `LineInput.amount` in totals.py: qty x unit price, rounded half-up to `places`. */
function lineAmount(line: LineInput, places: RoundingPlaces = 2): Dec {
  return qn(mul(dec(line.qty), dec(line.unitPrice)), places)
}

/** A line's Amount as the server stores it and `computeTotals` sums it: exact decimals rounded 2dp half-up, so the Amount column foots to the Subtotal. Not `roundMoney` (float: 1.005 gives 1.00 there, 1.01 here) and never a truncation. */
export function lineAmountOf(qty: number, unitPrice: number, places: RoundingPlaces = 2): number {
  return num(lineAmount({ qty, unitPrice }, places))
}

/** `mnt_equivalent` in totals.py: the total times the MNT rate, rounded half-up to `places`. */
export function mntTotalOf(total: number, rate: number, places: RoundingPlaces = 2): number {
  return num(qn(mul(dec(total), dec(rate)), places))
}

/** A unit price carries at most this many decimals (Numeric(18, 5)). */
const UNIT_PRICE_DECIMALS = 5

/** Whether `value` has at most `UNIT_PRICE_DECIMALS` decimals; the server answers 422 to a 6th. Read off `String(value)`, so 1e-7 counts as 7 decimals. */
export function unitPriceDecimalsOk(value: number): boolean {
  if (!Number.isFinite(value)) return false
  return dec(value).s <= UNIT_PRICE_DECIMALS
}

export function computeTotals(
  lines: LineInput[],
  options: {
    salesType: SalesType
    discountType?: DiscountType
    discount?: number
    vatType?: VatType
    places?: RoundingPlaces
  },
): Totals {
  const { salesType, discountType = 'None', discount = 0, vatType = '0%', places = 2 } = options
  const round = (a: Dec): Dec => qn(a, places)

  // Half-up, deliberately: the live Power Apps Finance rounds subtotal and VAT UP, but half-up was ruled (a document can differ from it by up to 0.01). See totals.py's module docstring.
  const subtotal = round(lines.reduce((sum, line) => add(sum, lineAmount(line, places)), ZERO))

  // Inventory expenditure orders carry no VAT and no discount.
  if (salesType === 'Inventory expenditure order') {
    const s = num(subtotal)
    return { subtotal: s, discountValue: 0, net: s, vat: 0, total: s }
  }

  let discountValue: Dec = ZERO
  if (discountType === 'Amount') {
    // Clamp to [0, subtotal] exactly as totals.py does, so the live figure cannot disagree with the saved one.
    discountValue = min(round(max(ZERO, dec(discount))), subtotal)
  } else if (discountType === 'Percentage') {
    const capped = min(max(dec(discount), ZERO), dec(100))
    discountValue = round(shift(mul(subtotal, capped), 2))
  }

  const net = round(sub(subtotal, discountValue))
  const vat = vatType === '10%' ? round(shift(net, 1)) : ZERO
  const total = round(add(net, vat))

  return {
    subtotal: num(subtotal),
    discountValue: num(discountValue),
    net: num(net),
    vat: num(vat),
    total: num(total),
  }
}

/** #,##0.00, the format used throughout the original app; `places` for a document's chosen rounding. */
export function formatMoney(value: number | string, places: RoundingPlaces = 2): string {
  const numeric = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(numeric)) return places === 2 ? '0.00' : (0).toFixed(places)
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: places,
    maximumFractionDigits: places,
  })
}

/** A stored or form value as a rounding choice; anything else reads as the default 2. */
export function roundingPlacesOf(value: unknown): RoundingPlaces {
  const n = Number(value)
  return (ROUNDING_PLACES as readonly number[]).includes(n) ? (n as RoundingPlaces) : 2
}

/** A UNIT PRICE: thousands-grouped at the fewest decimals that state it, never fewer than 2 nor more than 5. Amounts and totals keep `formatMoney`, which would print 0.00001 as 0.00. Mirrors the PDF's `unit_price` filter. */
export function formatUnitPrice(value: number | string): string {
  const numeric = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(numeric)) return '0.00'
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: UNIT_PRICE_DECIMALS,
  })
}

/** `formatMoney`, except a figure never recorded renders as an em dash rather than `0.00`.
 *  ⛔ Exists because `formatMoney` cannot change: the sales editor depends on its `'0.00'` for a half-typed amount, but on a payslip the same fallback printed a confident zero for segments with no rate by construction.
 *  A genuine `0` still renders `0.00`; test `== null` plus `Number.isFinite`, never falsiness (`0` is falsy and `''` numbers to zero). */
export function formatMoneyOrDash(value: number | string | null | undefined): string {
  if (value == null || value === '') return '—'
  const numeric = typeof value === 'string' ? Number(value) : value
  return Number.isFinite(numeric) ? formatMoney(numeric) : '—'
}

/** `formatMoney` with one decimal, for compact displays of already round figures. Not a precision change: callers showing real cents keep `formatMoney`. */
export function formatMoneyShort(value: number | string): string {
  const numeric = typeof value === 'string' ? Number(value) : value
  if (!Number.isFinite(numeric)) return '0.0'
  return numeric.toLocaleString('en-US', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })
}

export const CURRENCY_SYMBOLS: Record<string, string> = {
  MNT: '₮',
  USD: '$',
  KZT: '₸',
}

/** The codes offered first in a currency picker. NOT a whitelist: the server takes any three letters, and `currencyOptions` always adds the value already on the record. */
export const COMMON_CURRENCIES = [
  'MNT', 'USD', 'KZT', 'EUR', 'CNY', 'RUB', 'JPY', 'KRW', 'GBP', 'AUD', 'CAD', 'CHF',
] as const

/** Typed text -> an ISO-4217-shaped code (" eur " -> "EUR"), or null. Shape only; the server checks the same and no more. */
export function normalizeCurrencyCode(raw: string | null | undefined): string | null {
  const code = (raw ?? '').trim().toUpperCase()
  return /^[A-Z]{3}$/.test(code) ? code : null
}

/** The options a currency select offers: the common codes plus the current value when absent. ⛔ Without that, a EUR document would open showing MNT and Save would rewrite it. */
export function currencyOptions(current: string | null | undefined): string[] {
  const options: string[] = [...COMMON_CURRENCIES]
  const code = (current ?? '').trim()
  if (code && !options.includes(code)) options.push(code)
  return options
}

/** "USD $", "EUR": the code, and its symbol when one is known. */
export function currencyOptionLabel(code: string): string {
  const symbol = CURRENCY_SYMBOLS[code]
  return symbol ? `${code} ${symbol}` : code
}

/** A money figure with its currency: `1,000.00 ₮`. An unrecognised code prints as the code itself, never falling back to ₮, which would make an unknown currency masquerade as MNT; a blank code prints the bare figure. */
export function formatMoneyIn(
  value: number | string,
  currency: string | null | undefined,
  places: RoundingPlaces = 2,
): string {
  const code = (currency ?? '').trim().toUpperCase()
  if (!code) return formatMoney(value, places)
  return `${formatMoney(value, places)} ${CURRENCY_SYMBOLS[code] ?? code}`
}
